/* eslint-disable max-len */
/* eslint-disable jsx-a11y/no-autofocus */
import {
  Modal,
  Tab,
  Tabs,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  Grid,
} from '@mui/material';
import { Box } from '@mui/system';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useState,
  useRef,
} from 'react';
import api from '../../api/base';
import {
  addEntryToHotlist,
  entryUpdate,
  hotlistFields,
  searchEntries,
} from '../../api/paths';
import { CustomButton } from '../CustomButton';
import {
  AddPlateButton,
  AutoCompleteInput,
  AutoCompleteWrapperStyled,
  ChipStyled,
  CrossIcon,
  ExpTimeContainer,
  HotlistModalH1,
  HotlistModalWrapper,
  NotesContainer,
  PlateInput,
  StyledInputNotes,
  DashboardTabs,
  HotlistModalH2,
} from './HotlistModal.styles';
import {
  HotlistType,
  AlertTypeType,
  RegionType,
  ExpTimeType,
} from './HotlistModal.types';
import { ReactComponent as CheckmarkPermission } from '../../assets/img/icons/checkmark-permission.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/base/edit-2.svg';
import { ReactComponent as TrashIcon } from '../../assets/icons/base/trash-2.svg';
import { addSnackbar } from '../../redux/slices/snackbar';
import { useDispatch } from 'react-redux';
import {
  Entry,
  EntryResponse,
  HotlistsFieldsResponseType,
} from '../../api/paths.types';
import { TabPanel } from '../../pages/Dashboard/Tabs';
import {
  HeaderCell,
  IconButtonWrapper,
  MainCell,
} from '../../pages/Configuration/Configuration.styled';
import moment from 'moment';
import UpdateEntryModal, {
  UpdateEntryModalRefType,
} from '../../pages/Configuration/HotlistsTab/SingleHotlist/UpdateEntryModal/UpdateEntryModal';
import { DefaultFieldsType } from '../../pages/Configuration/HotlistsTab/SingleHotlist/SingleHotlist.types';
import { initFieldsForDef } from '../../pages/Configuration/HotlistsTab/SingleHotlist/SingleHotlist.contants';
import { CircleProgress } from '../common/CircleProgress';
import InfiniteScroll from 'react-infinite-scroll-component';
import ReadMoreText from '../ReadMoreText';
import DeleteModal from './DeleteModal';

export type HotlistModalRefType = {
  openModal: () => void;
  isOpen: boolean;
};

const defaultErrors = { plate: false, hotlist: false, alertType: false };

const HotlistModal = forwardRef<HotlistModalRefType>((_, ref) => {
  const [open, setOpen] = useState<boolean>(false);
  const [hotlists, setHotlists] = useState<HotlistType[]>([]);
  const [alertTypes, setAlertTypes] = useState<AlertTypeType[]>([]);
  const [regions, setRegions] = useState<RegionType[]>([]);

  const [expiration, setExpiration] = useState<ExpTimeType>({
    id: 0,
    text: '24 Hours',
    value: 86400,
  });

  const [notes, setNotes] = useState<string | null>(null);
  const [plate, setPlate] = useState<string>('');
  const [state, setState] = useState<RegionType | null>(null);
  const [hotlist, setHotlist] = useState<HotlistType | null>(null);
  const [alertType, setAlertType] = useState<AlertTypeType | null>(null);
  const [sameAlertError, setSameAlertError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const [error, setError] = useState(defaultErrors);

  const dispatch = useDispatch();

  const expirationTimes: ExpTimeType[] = [
    {
      id: 0,
      text: '24 Hours',
      value: 86400,
    },
    {
      id: 1,
      text: '1 Week',
      value: 604800,
    },
    {
      id: 2,
      text: '1 Month',
      value: 2592000,
    },
    {
      id: 3,
      text: 'Unlimited',
      value: 0,
    },
  ];

  useImperativeHandle(ref, () => ({
    openModal: () => {
      setOpen(true);
    },
    isOpen: open,
  }));

  useEffect(() => {
    if (open) fetchFields();
  }, [open]);

  useEffect(() => {
    const expirationTime = expirationTimes.find(
      (ex) => ex.value === hotlist?.expiration,
    );
    if (expirationTime) setExpiration(expirationTime);
  }, [hotlist]);

  const fetchFields = async () => {
    const { data } = await api.get<HotlistsFieldsResponseType>(hotlistFields);

    setHotlists(data.hotlists);
    setHotlist(data.hotlists?.[0] || null);
    setAlertTypes(data.alert_types.reverse());
    setAlertType(
      data.alert_types.find(
        (_alertType) => _alertType.id === (data.hotlists?.[0].alert_type as number),
      ) || null,
    );
    const newRegions = data.regions.reduce((acc: RegionType[], oldReg) => {
      if (oldReg.code === 'ca') return acc;

      const newReg = {
        ...oldReg,
        code: oldReg.code.slice(3).toUpperCase(),
      } as RegionType;
      acc.push(newReg);

      return acc;
    }, []);
    setRegions(newRegions);
  };

  const handleClose = () => {
    setOpen(false);
    setNotes(null);
    setPlate('');
    setState(null);
    setHotlist(null);
    setAlertType(null);
    setError(defaultErrors);
  };

  const handlePlateChange = (e: any) => {
    const value = e.target.value.toUpperCase();
    const validatedPlate
      = value
        ?.match(/[a-zA-Z0-9]/g)
        ?.slice(0, 10)
        ?.join('') || '';
    setPlate(validatedPlate);
    setError((oldState) => ({ ...oldState, plate: false }));
    setSameAlertError(null);
  };

  const handleStateChange = (e: any, value: any, reason: string) => {
    if (reason === 'clear') setState(null);
    else setState(value);
    setSameAlertError(null);
  };

  const handleHotlistsChange = (e: any, value: any, reason: string) => {
    if (reason === 'clear') setHotlist(null);
    else {
      setHotlist(value);
      setAlertType(
        alertTypes.find((_alert_type) => _alert_type.id === value.alert_type)
          || alertType,
      );
      setError((oldState) => ({ ...oldState, hotlist: false }));
    }
    setSameAlertError(null);
  };

  const handleAlertTypeChange = (e: any, value: any, reason: string) => {
    if (reason === 'clear') setAlertType(null);
    else {
      setAlertType(value);
      setError((oldState) => ({ ...oldState, alertType: false }));
    }
    setSameAlertError(null);
  };

  const addPlateToHotlist = async () => {
    let notEnoughData = false;

    if (!plate) {
      setError((oldState) => ({ ...oldState, plate: true }));
      notEnoughData = true;
    }
    if (!hotlist) {
      setError((oldState) => ({ ...oldState, hotlist: true }));
      notEnoughData = true;
    }

    if (!notEnoughData) {
      setLoading(true);
      try {
        await api.post(addEntryToHotlist, {
          plate,
          hotlist: hotlist?.id,
          notes,
          expiration: expiration.value,
          region: state?.id,
          alert_type: alertType?.id,
        });
        handleClose();
        dispatch(
          addSnackbar({
            message: 'Plate Added to Hotlist',
            options: { variant: 'success' },
          }),
        );
      } catch (err: any) {
        const er = err.response?.data;
        if (err.response.status === 400) {
          setSameAlertError(er?.detail);
        } else {
          dispatch(addSnackbar({ message: er?.detail }));
        }
      }
      setLoading(false);
    }
  };

  const [activeTab, setActiveTab] = useState<number>(0);

  const [entriesLoading, setEntriesLoading] = useState<boolean>(false);
  const [elements, setElements] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [entries, setEntries] = useState<Entry[]>([]);
  const fetchEntries = async (pageNumber?: number) => {
    try {
      setEntriesLoading(true);
      const { data } = await api.get<EntryResponse>(searchEntries, {
        params: {
          hotlist: hotlist?.id,
          page: pageNumber || currentPage,
        },
      });
      setCurrentPage((currPage) => currPage + 1);
      setEntries(pageNumber || currentPage === 1 ? data.results : [...entries, ...data.results]);
      setElements(data.count);
      // setEntriesLoading(false);
    } catch (err: any) {
      const er = err.response?.data;
      if (er?.detail) {
        dispatch(addSnackbar({ message: er?.detail }));
      }
    } finally {
      setEntriesLoading(false);
    }
  };

  useEffect(() => {
    if (hotlist && activeTab === 1) {
      setEntriesLoading(true);
      setEntries([]);
      setCurrentPage(1);
      fetchEntries(1);
    }
  }, [hotlist, activeTab]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteEntry, setDeleteEntry] = useState<Entry | null>(null);
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteEntry(null);
    setDeleteLoading(false);
  };
  const openDeleteModal = () => {
    setDeleteModalOpen(true);
  };
  const deleteEntryCall = (entry: Entry) => {
    setDeleteEntry(entry);
    openDeleteModal();
  };
  const handleDelete = async () => {
    if (deleteEntry) {
      setDeleteLoading(true);
      await api.delete(`${entryUpdate}${deleteEntry.id}/`);
      setEntries(
        entries.filter((_e) => _e.id !== deleteEntry.id),
      );
      setElements((ele) => ele - 1);
      closeDeleteModal();
    }
  };

  const entryModalRef = useRef<UpdateEntryModalRefType>(null);
  const [defaultfields, setDefaultFields]
    = useState<DefaultFieldsType>(initFieldsForDef);
  const getDefaultFields = async () => {
    const { data } = await api.get<HotlistsFieldsResponseType>(hotlistFields);
    const newRegions = data.regions.reduce((acc: RegionType[], oldReg) => {
      if (oldReg.code === 'ca') return acc;

      const newReg = {
        ...oldReg,
        code: oldReg.code.slice(3).toUpperCase(),
      } as RegionType;
      acc.push(newReg);

      return acc;
    }, []);

    setDefaultFields({
      states: newRegions,
      alertTypes: data.alert_types,
      expTime: expirationTimes,
      clients: data.clients,
      cameras: data.cameras,
    });
  };
  useEffect(() => {
    getDefaultFields();
  }, []);

  const getDuration = (date: Date) => {
    const duration = moment.duration(moment(date).diff(moment()));
    const hours = duration.asHours();
    if (hours < 0) {
      return 'Expired';
    }
    if (hours < 24) {
      return `${hours.toFixed()}h`;
    }
    if (hours < 8760) {
      return `${duration.asDays().toFixed()}d`;
    }
    return 'Unlim';
  };

  const getPlateState = (ent: Entry | null) => {
    if (ent) {
      return `${ent.plate}/${ent.region?.code?.split('-')?.[1]?.toUpperCase()}`;
    }
    return '';
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <HotlistModalWrapper sx={{ bgcolor: 'primary.theme' }}>
          <HotlistModalH1 sx={{ color: 'text.primary' }}>
            Quick Hotlist Entry
            <CrossIcon onClick={() => handleClose()} />
          </HotlistModalH1>
          <DashboardTabs>
            <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
              <Tab
                sx={{
                  color: 'text.primary',
                  width: '163px',
                }}
                label={'Add an Entry'}
              />
              <Tab
                sx={{
                  color: 'text.primary',
                  width: '163px',
                }}
                label={'View Entries'}
              />
            </Tabs>
          </DashboardTabs>
          <TabPanel value={activeTab} index={0}>
            <Box display="flex" flexDirection="row" gap="14px">
              <PlateInput
                value={plate}
                error={error.plate}
                disableUnderline
                placeholder="Plate"
                autoFocus
                onChange={handlePlateChange}
                sx={{ bgcolor: 'primary.input' }}
              />
              <AutoCompleteWrapperStyled
                id="state-box"
                disablePortal
                options={regions}
                sx={{ width: 129, bgcolor: 'primary.input' }}
                getOptionLabel={(option: any) => option.code}
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id
                }
                onChange={handleStateChange}
                popupIcon={<KeyboardArrowDownIcon />}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                renderInput={(params) => (
                  <AutoCompleteInput placeholder="State" {...params} />
                )}
              />
              <AutoCompleteWrapperStyled
                id="hotlists-box"
                disablePortal
                options={hotlists}
                sx={{ width: 275.5, bgcolor: 'primary.input' }}
                getOptionLabel={(option: any) => option.name}
                value={hotlist}
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id
                }
                onChange={handleHotlistsChange}
                popupIcon={<KeyboardArrowDownIcon />}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                renderInput={(params) => (
                  <AutoCompleteInput
                    error={error.hotlist}
                    placeholder="Hotlists"
                    sx={{ bgcolor: 'primary.input' }}
                    {...params}
                  />
                )}
              />
              <AutoCompleteWrapperStyled
                id="alarm-box"
                disablePortal
                options={alertTypes}
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id
                }
                getOptionLabel={(option: any) => option.name}
                onChange={handleAlertTypeChange}
                value={alertType}
                sx={{ bgcolor: 'primary.input', width: 275.5 }}
                popupIcon={<KeyboardArrowDownIcon />}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                renderInput={(params) => (
                  <AutoCompleteInput
                    sx={{ bgcolor: 'primary.input' }}
                    error={error.alertType}
                    placeholder="Alarm Class"
                    {...params}
                  />
                )}
              />
            </Box>
            <Box display="flex" mt="28px" gap="20px">
              <NotesContainer>
                <HotlistModalH2 sx={{ color: 'text.primary' }}>
                  Notes
                </HotlistModalH2>
                <StyledInputNotes
                  placeholder="Write a note..."
                  inputProps={{ maxLength: 300 }}
                  rows={4}
                  multiline
                  disableUnderline
                  onChange={(e) => setNotes(e.target.value)}
                  sx={{ bgcolor: 'primary.input' }}
                />
              </NotesContainer>
              <ExpTimeContainer>
                <HotlistModalH2 sx={{ color: 'text.primary' }}>
                  Expiration Time
                </HotlistModalH2>
                <Box
                  display="inline-flex"
                  flexWrap="wrap"
                  flexDirection="row"
                  rowGap="14px"
                  columnGap="11px"
                >
                  {expirationTimes.map((expTime) => (
                    <ChipStyled
                      picked={expTime.id === expiration.id}
                      key={expTime.id}
                      onClick={() => setExpiration(expTime)}
                    >
                      <span>{expTime.text}</span>
                      {expTime.id === expiration.id && <CheckmarkPermission />}
                    </ChipStyled>
                  ))}
                </Box>
              </ExpTimeContainer>
            </Box>
            <Box mt={5}>
              <Typography color="error" fontWeight="bold">
                {sameAlertError}
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="flex-end"
              gap="20px"
              mt="48px"
              width="100%"
            >
              <CustomButton
                size="large"
                variant="outlined"
                color="primary"
                onClick={handleClose}
                sx={{
                  fontWeight: 500,
                  padding: '10px 18px',
                  border: 'none !important',
                  outline: 'none !important',
                }}
              >
                Cancel
              </CustomButton>
              <AddPlateButton
                onClick={addPlateToHotlist}
                disabled={!!sameAlertError}
                variant="contained"
                color="primary"
                loading={loading}
              >
                Add plate
              </AddPlateButton>
            </Box>
          </TabPanel>
          <TabPanel value={activeTab} index={1} allowHide>
            <span style={{ width: '100%' }}>
              <HotlistModalH2 sx={{ color: 'text.primary' }}>
                Select Hotlist
              </HotlistModalH2>
              <AutoCompleteWrapperStyled
                id="hotlists-box"
                disablePortal
                options={hotlists}
                sx={{ width: '100%', bgcolor: 'primary.input' }}
                getOptionLabel={(option: any) => option.name}
                value={hotlist}
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id
                }
                onChange={handleHotlistsChange}
                popupIcon={<KeyboardArrowDownIcon />}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                renderInput={(params) => (
                  <AutoCompleteInput
                    error={error.hotlist}
                    placeholder="Select Hotlist"
                    sx={{ bgcolor: 'primary.input' }}
                    {...params}
                  />
                )}
              />
            </span>
            {!!entries?.length && (
              <>
                <HotlistModalH2
                  sx={{ color: 'text.primary', marginTop: '1.5rem' }}
                >
                  Entries
                </HotlistModalH2>
                <TableContainer
                  maxHeight="384px"
                  sx={{
                    '@media screen and (max-height: 800px)': {
                      maxHeight: '300px',
                    },
                    table: {
                      borderCollapse: 'collapse',
                    },
                  }}
                  bgcolor={'base.background'}
                  component={Box}
                  borderRadius="8px"
                  id="infinite-scroll-entries"
                >
                  <InfiniteScroll
                    style={{ overflow: 'hidden' }}
                    scrollableTarget="infinite-scroll-entries"
                    dataLength={entries.length}
                    next={() => fetchEntries()}
                    hasMore={elements > entries.length}
                    scrollThreshold={1}
                    loader={
                      entriesLoading && (
                        <Grid
                          alignContent={'center'}
                          justifyContent={'center'}
                          item
                          sm={12}
                          md={6}
                          lg={6}
                          xl={4}
                        >
                          <Box
                            display={'flex'}
                            justifyContent={'center'}
                            alignItems={'center'}
                          >
                            <CircleProgress />
                          </Box>
                        </Grid>
                      )
                    }
                  >
                    <Table stickyHeader>
                      <TableHead sx={{ bgcolor: 'grey.50' }}>
                        <HeaderCell>
                          <Typography variant={'txs'} color={'grey.500'}>
                            License Plate/State
                          </Typography>
                        </HeaderCell>
                        <HeaderCell>
                          <Typography variant={'txs'} color={'grey.500'}>
                            Alarm Class
                          </Typography>
                        </HeaderCell>
                        <HeaderCell>
                          <Typography variant={'txs'} color={'grey.500'}>
                            Exp. Time
                          </Typography>
                        </HeaderCell>
                        <HeaderCell>
                          <Typography variant={'txs'} color={'grey.500'}>
                            Notes
                          </Typography>
                        </HeaderCell>
                        <HeaderCell />
                      </TableHead>
                      <TableBody>
                        {entries.map((ent) => (
                          <TableRow
                            key={ent.id}
                            sx={{
                              borderBottom: '1px solid',
                              borderColor: 'grey.200',
                            }}
                          >
                            <MainCell>
                              <Typography variant={'tsm'} fontWeight={'500'}>
                                {getPlateState(ent)}
                              </Typography>
                            </MainCell>
                            <MainCell>
                              <Typography color="grey.500" variant="tsm">
                                {ent.alert_type.name}
                              </Typography>
                            </MainCell>
                            <MainCell>
                              <Typography color="grey.500" variant="tsm">
                                {getDuration(new Date(ent.expired))}
                              </Typography>
                            </MainCell>
                            <MainCell
                              sx={{
                                width: '322px',
                                padding: '9px 24px !important',
                              }}
                            >
                              <Typography color="grey.500" variant="tsm">
                                <ReadMoreText text={ent.notes} />
                              </Typography>
                            </MainCell>
                            <MainCell
                              sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <Typography color="grey.500" variant="tsm">
                                <IconButtonWrapper
                                  onClick={() => entryModalRef.current?.openModal(ent)
                                  }
                                  sx={{ mr: 1 }}
                                >
                                  <EditIcon />
                                </IconButtonWrapper>
                                <IconButtonWrapper
                                  onClick={() => deleteEntryCall(ent)}
                                  sx={{ mr: 1 }}
                                >
                                  <TrashIcon />
                                </IconButtonWrapper>
                              </Typography>
                            </MainCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </InfiniteScroll>
                </TableContainer>
              </>
            )}
          </TabPanel>
        </HotlistModalWrapper>
      </Modal>
      <UpdateEntryModal
        defaultFields={defaultfields}
        fetchEntries={fetchEntries}
        deleteEntry={deleteEntryCall}
        ref={entryModalRef}
        onUpdate={fetchEntries}
      />
      <DeleteModal
        plateState={getPlateState(deleteEntry)}
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        deleteLoading={deleteLoading}
        onDelete={handleDelete}
      />
    </>
  );
});

export default memo(HotlistModal);
